import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { NEXT_PARAM, PUBLIC_ROUTES } from '@/lib/route.constant';
import { selectIsAuthenticated } from '@/shared/store/reducers/auth/auth.slice';
import { useAppSelector } from '@/shared/store/typed-hooks/typed-hooks';

/**
 * The gate on every route that is not `/login` (W1).
 *
 * It checks only whether a session EXISTS — never whether it is still valid.
 * Validity is the api's answer, delivered as a 401 that `baseQueryWithAuth`
 * turns into one refresh attempt and, on failure, a `loggedOut` dispatch that
 * flows back here. Trying to judge expiry from the token's `exp` on this side
 * would put a second, drifting authority next to the real one.
 *
 * The path the operator was heading for is carried in `?next=`, and read back
 * through `safeRedirect` — see that util for why a bare `startsWith('/')` check
 * is an open redirect.
 */
export function ProtectedRoute() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const location = useLocation();

  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}${location.hash}`;
    const target =
      next === '/'
        ? PUBLIC_ROUTES.LOGIN
        : `${PUBLIC_ROUTES.LOGIN}?${NEXT_PARAM}=${encodeURIComponent(next)}`;

    // `replace` so Back does not bounce between the guard and the login page.
    return <Navigate to={target} replace />;
  }

  return <Outlet />;
}
